import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Button from "../../../../components/Button";
import Input from "../../../../components/Input";
import Dropdown from "../../../../components/Dropdown";
import TextArea from "../../../../components/TextArea";
import { AddEdit as AddEditInterface } from "../../../../interfaces/payment.interface";
import { PAYMENT_ACCOUNTS, VALIDATION_FIELD_MAPPING } from "../../../../utils/appConstant";
import { Validation } from "../../../../interfaces/common";

interface AddEditProps {
  formData: AddEditInterface;
  validation: Validation;
  methods: any[];
  allMethods: any[];
  accounts: any[];
  allAccounts: any[];
  paymentTypeOptions: any[];
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
  onInputBlur: (e: any, name: string) => void;
}

const AddEdit: React.FC<AddEditProps> = ({
  formData,
  validation,
  methods,
  allMethods,
  accounts,
  allAccounts,
  paymentTypeOptions,
  dropdownSearchData,
  onDropdownSearchChange,
  onInputChange,
  onInputBlur
}) => {
  const { t } = useTranslation();

  return (
    <form className="card-body" autoComplete="off">
      <div className="row g-6">
        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Input
              type="date"
              value={formData.date}
              required
              id={VALIDATION_FIELD_MAPPING.date}
              name={VALIDATION_FIELD_MAPPING.date}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.date)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.date)?.message ?? ''}
              label={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.date.label')}
              placeholder={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.date.placeholder')}
              onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.date)}
              onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.date)}
            />
          </div>
        </div>

        <div className="col-md-3">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.amount}
              name={VALIDATION_FIELD_MAPPING.amount}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.amount)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.amount)?.message ?? ''}
              label={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.amount.label')}
              placeholder={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.amount.placeholder')}
              value={formData.amount}
              onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.amount)}
              onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.amount)}
            />
          </div>
        </div>

         <div className="col-md-6">
           <div className="form-floating form-floating-outline">
             <div className="form-control btn-group w-100 p-0">
               <Button
                 className={
                   clsx(
                     "btn btn-sm h-100 flex-fill shadow-none border-0 text-uppercase fw-semibold",
                     formData.type === (paymentTypeOptions[0]?.value || 'received') && 'btn-primary',
                     formData.type !== (paymentTypeOptions[0]?.value || 'received') && 'text-primary'
                   )
                 }
                 text={paymentTypeOptions[0]?.label || t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.paymentType.received')}
                 onClick={() => onInputChange({ target: { value: paymentTypeOptions[0]?.value || 'received' } }, VALIDATION_FIELD_MAPPING.type)}
               />
               <Button
                 className={
                   clsx(
                     "btn btn-sm h-100 flex-fill shadow-none border-0 text-uppercase fw-semibold",
                     formData.type === (paymentTypeOptions[1]?.value || 'paid') && 'btn-success',
                     formData.type !== (paymentTypeOptions[1]?.value || 'paid') && 'text-primary'
                   )
                 }
                 text={paymentTypeOptions[1]?.label || t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.paymentType.paid')}
                 onClick={() => onInputChange({ target: { value: paymentTypeOptions[1]?.value || 'paid' } }, VALIDATION_FIELD_MAPPING.type)}
               />
               <Button
                 className={
                   clsx(
                     "btn btn-sm h-100 flex-fill shadow-none border-0 text-uppercase fw-semibold",
                     formData.type === (paymentTypeOptions[2]?.value || 'carry_forward') && 'btn-warning',
                     formData.type !== (paymentTypeOptions[2]?.value || 'carry_forward') && 'text-primary'
                   )
                 }
                 text={'Forward'}
                 onClick={() => onInputChange({ target: { value: paymentTypeOptions[2]?.value || 'carry_forward' } }, VALIDATION_FIELD_MAPPING.type)}
               />
             </div>
           </div>
         </div>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Dropdown
              id={VALIDATION_FIELD_MAPPING.method}
              name={VALIDATION_FIELD_MAPPING.method}
              required
              disabled={formData.type === (paymentTypeOptions[2]?.value || 'carry_forward')}
              values={formData.method ? [formData.method] : []}
              options={(methods || []).map((item) => ({
                label: item.name,
                value: item.id
              }))}
              allOptions={(allMethods || []).map((item) => ({
                label: item.name,
                value: item.id
              }))}
              label={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.method.label')}
              placeholder={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.method.placeholder')}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.method)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.method)?.message ?? ''}
              searchText={dropdownSearchData.method}
              onSearch={onDropdownSearchChange}
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Dropdown
              id={VALIDATION_FIELD_MAPPING.account}
              name={VALIDATION_FIELD_MAPPING.account}
              disabled={formData.type === (paymentTypeOptions[2]?.value || 'carry_forward')}
              values={formData.account ? [formData.account] : []}
              options={(accounts || []).map((item) => ({
                label: item.name,
                value: item.id
              }))}
              allOptions={(allAccounts || []).map((item) => ({
                label: item.name,
                value: item.id
              }))}
              label={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.account.label')}
              placeholder={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.account.placeholder')}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.account)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.account)?.message ?? ''}
              required
              searchText={dropdownSearchData.account}
              onSearch={onDropdownSearchChange}
              onChange={onInputChange}
              onBlur={onInputBlur}
            />
          </div>
        </div>

        <div className="col-md-12">
          <div className="form-floating form-floating-outline">
            <TextArea
              id={VALIDATION_FIELD_MAPPING.notes}
              name={VALIDATION_FIELD_MAPPING.notes}
              label={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.notes.label')}
              placeholder={t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.modal.addPayment.fields.notes.placeholder')}
              value={formData.notes}
              rows={4}
              className="form-control-lg h-100"
              required={formData.type === (paymentTypeOptions[2]?.value || 'carry_forward') || formData.account === (allAccounts || []).find(item => item.code === PAYMENT_ACCOUNTS.OTHER)?.id}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.notes)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.notes)?.message ?? ''}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.notes)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.notes)}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default AddEdit;
